import { 
  Shield, 
  TrendingUp, 
  Users, 
  Code, 
  Building2, 
  Target, 
  Scale, 
  AlertTriangle,
  MessageSquare,
  UserCheck,
  ChevronDown,
  ChevronUp,
  CheckCircle2,
  XCircle,
  Info
} from 'lucide-react';
import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { FundamentalDueDiligence } from '@/hooks/useDeepAnalysis';
import { cn } from '@/lib/utils';
import { DDRadarChart } from './DDRadarChart';

interface FundamentalDDCardProps {
  dd: FundamentalDueDiligence;
}

const getScoreColor = (score: number) => {
  if (score >= 70) return 'text-green-500';
  if (score >= 50) return 'text-yellow-500';
  if (score >= 30) return 'text-orange-500';
  return 'text-red-500';
};

const getScoreBg = (score: number) => {
  if (score >= 70) return 'bg-green-500/20';
  if (score >= 50) return 'bg-yellow-500/20';
  if (score >= 30) return 'bg-orange-500/20';
  return 'bg-red-500/20';
};

const getGradeColor = (grade: string) => {
  switch (grade) {
    case 'A': return 'bg-green-500/20 text-green-500 border-green-500/30';
    case 'B': return 'bg-emerald-500/20 text-emerald-500 border-emerald-500/30';
    case 'C': return 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30';
    case 'D': return 'bg-orange-500/20 text-orange-500 border-orange-500/30';
    case 'F': return 'bg-red-500/20 text-red-500 border-red-500/30';
    default: return 'bg-muted text-muted-foreground';
  }
};

const getRatingBadge = (rating: string) => {
  const positiveRatings = ['Strong', 'High', 'Excellent', 'Mostly Organic', 'Growing', 'Undervalued', 'Good', 'Low'];
  const neutralRatings = ['Moderate', 'Medium', 'Fair', 'Mixed', 'Stable'];
  
  if (positiveRatings.includes(rating)) {
    return 'bg-green-500/20 text-green-500 border-green-500/30';
  }
  if (neutralRatings.includes(rating)) {
    return 'bg-yellow-500/20 text-yellow-500 border-yellow-500/30';
  }
  return 'bg-red-500/20 text-red-500 border-red-500/30';
};

interface DDSectionProps {
  title: string;
  icon: React.ReactNode;
  score: number;
  children: React.ReactNode;
  defaultOpen?: boolean;
}

const DDSection = ({ title, icon, score, children, defaultOpen = false }: DDSectionProps) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  
  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger className="w-full">
        <div className="flex items-center justify-between p-3 bg-card/50 rounded-lg border border-border hover:bg-card transition-colors">
          <div className="flex items-center gap-3">
            <div className={cn("p-2 rounded-lg", getScoreBg(score))}>
              {icon}
            </div>
            <span className="font-medium text-sm">{title}</span>
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <span className={cn("font-bold text-lg", getScoreColor(score))}>{score}</span>
              <span className="text-xs text-muted-foreground">/100</span>
            </div>
            {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </div>
        </div>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="mt-2 p-4 bg-muted/30 rounded-lg border border-border/50 space-y-3">
          {children}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};

const KeyFinding = ({ finding }: { finding: string }) => (
  <div className="flex items-start gap-2 text-sm">
    <Info className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
    <span className="text-muted-foreground">{finding}</span>
  </div>
);

export function FundamentalDDCard({ dd }: FundamentalDDCardProps) {
  return (
    <Card className="border-primary/20">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Shield className="w-6 h-6 text-primary" />
            </div>
            <div>
              <CardTitle className="text-lg">Fundamental Due Diligence</CardTitle>
              <p className="text-xs text-muted-foreground mt-1">Long-term investment assessment</p>
            </div>
          </div>
          <div className="text-right">
            <Badge className={cn("text-xl px-3 py-1", getGradeColor(dd.ddGrade))}>
              Grade: {dd.ddGrade}
            </Badge>
            <p className="text-sm text-muted-foreground mt-1">
              Overall Score: <span className={cn("font-bold", getScoreColor(dd.overallDDScore))}>{dd.overallDDScore}/100</span>
            </p>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Investment Thesis */}
        <div className="p-4 bg-primary/5 rounded-lg border border-primary/20">
          <h4 className="font-semibold text-sm mb-2 text-primary">Investment Thesis</h4>
          <p className="text-sm text-muted-foreground">{dd.investmentThesis}</p>
        </div>

        {/* Radar Chart */}
        <DDRadarChart dd={dd} />

        {/* Detailed Sections */}
        <div className="space-y-2">
          {/* Project Fundamentals */}
          {dd.projectFundamentals && (
            <DDSection 
              title="Project Fundamentals & Utility" 
              icon={<Building2 className="w-4 h-4 text-primary" />}
              score={dd.projectFundamentals.score}
              defaultOpen
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Problem Solved</p>
                  <p className="text-sm">{dd.projectFundamentals.problemSolved}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Solution Quality:</span>
                  <Badge className={getRatingBadge(dd.projectFundamentals.solutionQuality)}>
                    {dd.projectFundamentals.solutionQuality}
                  </Badge>
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Token Utility</p>
                <p className="text-sm">{dd.projectFundamentals.tokenUtility}</p>
                <Badge className={cn("mt-1", getRatingBadge(dd.projectFundamentals.utilityRating))}>
                  {dd.projectFundamentals.utilityRating}
                </Badge>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Traction Evidence</p>
                <p className="text-sm">{dd.projectFundamentals.tractionEvidence}</p>
              </div>
              <div className="space-y-1 pt-2 border-t border-border/50">
                {dd.projectFundamentals.keyFindings?.map((finding, i) => (
                  <KeyFinding key={i} finding={finding} />
                ))}
              </div>
            </DDSection>
          )}

          {/* Tokenomics */}
          {dd.tokenomics && (
            <DDSection 
              title="Tokenomics & Value Accrual" 
              icon={<Scale className="w-4 h-4 text-primary" />}
              score={dd.tokenomics.score}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Supply Details</p>
                  <p className="text-sm">{dd.tokenomics.totalSupply}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Dilution Risk:</span>
                  <Badge className={getRatingBadge(dd.tokenomics.dilutionRisk === 'Low' ? 'Low' : dd.tokenomics.dilutionRisk)}>
                    {dd.tokenomics.dilutionRisk}
                  </Badge>
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">MC vs FDV Analysis</p>
                <p className="text-sm">{dd.tokenomics.mcapVsFdv}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Value Accrual</p>
                <p className="text-sm">{dd.tokenomics.valueAccrual}</p>
                <Badge className={cn("mt-1", getRatingBadge(dd.tokenomics.valueAccrualRating))}>
                  {dd.tokenomics.valueAccrualRating}
                </Badge>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Distribution & Vesting</p>
                <p className="text-sm">{dd.tokenomics.distribution}</p>
                <p className="text-sm text-muted-foreground mt-1">{dd.tokenomics.vestingSchedule}</p>
              </div>
              <div className="space-y-1 pt-2 border-t border-border/50">
                {dd.tokenomics.keyFindings?.map((finding, i) => (
                  <KeyFinding key={i} finding={finding} />
                ))}
              </div>
            </DDSection>
          )}

          {/* Team & Execution */}
          {dd.teamAndExecution && (
            <DDSection 
              title="Team, Transparency & Execution" 
              icon={<Users className="w-4 h-4 text-primary" />}
              score={dd.teamAndExecution.score}
            >
              <div>
                <p className="text-xs text-muted-foreground mb-1">Team Background</p>
                <p className="text-sm">{dd.teamAndExecution.teamBackground}</p>
                <Badge className={cn("mt-1", getRatingBadge(dd.teamAndExecution.credibilityRating))}>
                  Credibility: {dd.teamAndExecution.credibilityRating}
                </Badge>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Roadmap Execution</p>
                <p className="text-sm">{dd.teamAndExecution.roadmapExecution}</p>
                <Badge className={cn("mt-1", getRatingBadge(dd.teamAndExecution.executionRating))}>
                  {dd.teamAndExecution.executionRating}
                </Badge>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Backing</p>
                <p className="text-sm">{dd.teamAndExecution.backing}</p>
              </div>
              <div className="space-y-1 pt-2 border-t border-border/50">
                {dd.teamAndExecution.keyFindings?.map((finding, i) => (
                  <KeyFinding key={i} finding={finding} />
                ))}
              </div>
            </DDSection>
          )}

          {/* Technology & Security */}
          {dd.technologyAndSecurity && (
            <DDSection 
              title="Technology, Security & Dev Activity" 
              icon={<Code className="w-4 h-4 text-primary" />}
              score={dd.technologyAndSecurity.score}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Audit Status</p>
                  <p className="text-sm">{dd.technologyAndSecurity.auditStatus}</p>
                  <Badge className={cn("mt-1", getRatingBadge(dd.technologyAndSecurity.securityRating))}>
                    Security: {dd.technologyAndSecurity.securityRating}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Dev Activity</p>
                  <p className="text-sm">{dd.technologyAndSecurity.devActivity}</p>
                  <Badge className={cn("mt-1", getRatingBadge(dd.technologyAndSecurity.devActivityRating))}>
                    {dd.technologyAndSecurity.devActivityRating}
                  </Badge>
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Tech Differentiation</p>
                <p className="text-sm">{dd.technologyAndSecurity.techDifferentiation}</p>
              </div>
              <div className="space-y-1 pt-2 border-t border-border/50">
                {dd.technologyAndSecurity.keyFindings?.map((finding, i) => (
                  <KeyFinding key={i} finding={finding} />
                ))}
              </div>
            </DDSection>
          )}

          {/* Adoption Metrics */}
          {dd.adoptionMetrics && (
            <DDSection 
              title="Adoption & On-Chain Metrics" 
              icon={<TrendingUp className="w-4 h-4 text-primary" />}
              score={dd.adoptionMetrics.score}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">User Growth</p>
                  <p className="text-sm">{dd.adoptionMetrics.userGrowth}</p>
                  <Badge className={cn("mt-1", getRatingBadge(dd.adoptionMetrics.growthTrend))}>
                    {dd.adoptionMetrics.growthTrend}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Organic vs Incentivized</p>
                  <p className="text-sm">{dd.adoptionMetrics.organicVsIncentivized}</p>
                  <Badge className={cn("mt-1", getRatingBadge(dd.adoptionMetrics.organicRating))}>
                    {dd.adoptionMetrics.organicRating}
                  </Badge>
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Revenue Generation</p>
                <p className="text-sm">{dd.adoptionMetrics.revenueGeneration}</p>
              </div>
              <div className="space-y-1 pt-2 border-t border-border/50">
                {dd.adoptionMetrics.keyFindings?.map((finding, i) => (
                  <KeyFinding key={i} finding={finding} />
                ))}
              </div>
            </DDSection>
          )}

          {/* Market Opportunity */}
          {dd.marketOpportunity && (
            <DDSection 
              title="Market Opportunity & Competition" 
              icon={<Target className="w-4 h-4 text-primary" />}
              score={dd.marketOpportunity.score}
            >
              <div>
                <p className="text-xs text-muted-foreground mb-1">Total Addressable Market</p>
                <p className="text-sm">{dd.marketOpportunity.tam}</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Competitive Position</p>
                  <p className="text-sm">{dd.marketOpportunity.competitivePosition}</p>
                  <Badge className={cn("mt-1", getRatingBadge(dd.marketOpportunity.moat))}>
                    Moat: {dd.marketOpportunity.moat}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Narrative Fit</p>
                  <p className="text-sm">{dd.marketOpportunity.narrativeFit}</p>
                  <Badge className={cn("mt-1", getRatingBadge(dd.marketOpportunity.narrativeStrength))}>
                    {dd.marketOpportunity.narrativeStrength}
                  </Badge>
                </div>
              </div>
              <div className="space-y-1 pt-2 border-t border-border/50">
                {dd.marketOpportunity.keyFindings?.map((finding, i) => (
                  <KeyFinding key={i} finding={finding} />
                ))}
              </div>
            </DDSection>
          )}

          {/* Valuation */}
          {dd.valuationAnalysis && (
            <DDSection 
              title="Valuation & Financial Health" 
              icon={<Scale className="w-4 h-4 text-primary" />}
              score={dd.valuationAnalysis.score}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Relative to Peers</p>
                  <p className="text-sm">{dd.valuationAnalysis.relativeToPeers}</p>
                  <Badge className={cn("mt-1", getRatingBadge(dd.valuationAnalysis.valuationRating))}>
                    {dd.valuationAnalysis.valuationRating}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Liquidity</p>
                  <p className="text-sm">{dd.valuationAnalysis.liquidity}</p>
                  <Badge className={cn("mt-1", getRatingBadge(dd.valuationAnalysis.liquidityRating))}>
                    {dd.valuationAnalysis.liquidityRating}
                  </Badge>
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Cyclical Resilience</p>
                <p className="text-sm">{dd.valuationAnalysis.cyclicalResilience}</p>
              </div>
              <div className="space-y-1 pt-2 border-t border-border/50">
                {dd.valuationAnalysis.keyFindings?.map((finding, i) => (
                  <KeyFinding key={i} finding={finding} />
                ))}
              </div>
            </DDSection>
          )}

          {/* Regulatory & Risks */}
          {dd.regulatoryAndRisks && (
            <DDSection 
              title="Regulatory, Legal & Risk Factors" 
              icon={<AlertTriangle className="w-4 h-4 text-primary" />}
              score={dd.regulatoryAndRisks.score}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Compliance Status</p>
                  <p className="text-sm">{dd.regulatoryAndRisks.complianceStatus}</p>
                  <Badge className={getRatingBadge(dd.regulatoryAndRisks.regulatoryRisk === 'Low' ? 'Low' : 'High')}>
                    Regulatory Risk: {dd.regulatoryAndRisks.regulatoryRisk}
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Smart Contract Risks</p>
                  <p className="text-sm">{dd.regulatoryAndRisks.contractRisks}</p>
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Centralization Risks</p>
                <p className="text-sm">{dd.regulatoryAndRisks.centralizationRisks}</p>
              </div>
              <div className="space-y-1 pt-2 border-t border-border/50">
                <p className="text-xs font-medium text-destructive">Key Risks:</p>
                {dd.regulatoryAndRisks.keyRisks?.map((risk, i) => (
                  <div key={i} className="flex items-start gap-2 text-sm">
                    <XCircle className="w-4 h-4 text-destructive mt-0.5 flex-shrink-0" />
                    <span className="text-muted-foreground">{risk}</span>
                  </div>
                ))}
              </div>
            </DDSection>
          )}

          {/* Community & Ecosystem */}
          {dd.communityAndEcosystem && (
            <DDSection 
              title="Community, Ecosystem & Catalysts" 
              icon={<MessageSquare className="w-4 h-4 text-primary" />}
              score={dd.communityAndEcosystem.score}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Community Engagement</p>
                  <p className="text-sm">{dd.communityAndEcosystem.communityEngagement}</p>
                  <Badge className={cn("mt-1", getRatingBadge(dd.communityAndEcosystem.engagementQuality))}>
                    {dd.communityAndEcosystem.engagementQuality} Quality
                  </Badge>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground mb-1">Governance</p>
                  <p className="text-sm">{dd.communityAndEcosystem.governanceParticipation}</p>
                </div>
              </div>
              <div>
                <p className="text-xs text-muted-foreground mb-1">Developer Ecosystem</p>
                <p className="text-sm">{dd.communityAndEcosystem.developerEcosystem}</p>
              </div>
              {dd.communityAndEcosystem.upcomingCatalysts?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-primary mb-2">Upcoming Catalysts:</p>
                  <div className="space-y-1">
                    {dd.communityAndEcosystem.upcomingCatalysts.map((catalyst, i) => (
                      <div key={i} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span className="text-muted-foreground">{catalyst}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </DDSection>
          )}

          {/* Investment Fit */}
          {dd.investmentFitAssessment && (
            <DDSection 
              title="Investment Fit Assessment" 
              icon={<UserCheck className="w-4 h-4 text-primary" />}
              score={70}
              defaultOpen
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <p className="text-xs font-medium text-green-500 mb-2">Suitable For:</p>
                  <ul className="space-y-1">
                    {dd.investmentFitAssessment.suitableFor?.map((item, i) => (
                      <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                        <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
                <div>
                  <p className="text-xs font-medium text-red-500 mb-2">Not Suitable For:</p>
                  <ul className="space-y-1">
                    {dd.investmentFitAssessment.notSuitableFor?.map((item, i) => (
                      <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                        <XCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
              <div className="flex flex-wrap gap-3 pt-2 border-t border-border/50">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Time Horizon:</span>
                  <Badge variant="outline">{dd.investmentFitAssessment.suggestedTimeHorizon}</Badge>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Suggested Allocation:</span>
                  <Badge variant="outline">{dd.investmentFitAssessment.suggestedAllocation}</Badge>
                </div>
              </div>
              {dd.investmentFitAssessment.exitTriggers?.length > 0 && (
                <div>
                  <p className="text-xs font-medium text-orange-500 mb-2">Exit Triggers (What Would Invalidate Thesis):</p>
                  <ul className="space-y-1">
                    {dd.investmentFitAssessment.exitTriggers.map((trigger, i) => (
                      <li key={i} className="text-sm text-muted-foreground flex items-start gap-2">
                        <AlertTriangle className="w-4 h-4 text-orange-500 mt-0.5 flex-shrink-0" />
                        {trigger}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </DDSection>
          )}
        </div>

        {/* DD Summary */}
        {dd.ddSummary && (
          <div className="mt-4 p-4 bg-muted/50 rounded-lg border border-border">
            <h4 className="font-semibold text-sm mb-3">DD Summary</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-3">
              <div>
                <p className="text-xs text-green-500 font-medium mb-1">Passed Categories (60+):</p>
                <div className="flex flex-wrap gap-1">
                  {dd.ddSummary.passedCategories?.map((cat, i) => (
                    <Badge key={i} className="bg-green-500/20 text-green-500 border-green-500/30 text-xs">
                      {cat}
                    </Badge>
                  ))}
                </div>
              </div>
              <div>
                <p className="text-xs text-red-500 font-medium mb-1">Failed Categories (&lt;50):</p>
                <div className="flex flex-wrap gap-1">
                  {dd.ddSummary.failedCategories?.map((cat, i) => (
                    <Badge key={i} className="bg-red-500/20 text-red-500 border-red-500/30 text-xs">
                      {cat}
                    </Badge>
                  ))}
                </div>
              </div>
            </div>
            <div className="pt-3 border-t border-border/50">
              <p className="text-xs text-muted-foreground mb-1 font-medium">Overall Assessment:</p>
              <p className="text-sm">{dd.ddSummary.overallAssessment}</p>
            </div>
          </div>
        )}

        {/* Disclaimer */}
        <div className="text-xs text-muted-foreground text-center pt-2">
          <p>⚠️ Most crypto tokens fail long-term. This DD analysis is AI-generated based on available data and should not be considered financial advice. Always DYOR.</p>
        </div>
      </CardContent>
    </Card>
  );
}
